import type {
  RendererProps,
  UISchemaElement,
  VerticalLayout,
} from '@jsonforms/core';
import { useJsonForms } from '@jsonforms/react';
import type { FunctionComponent } from 'react';
import React from 'react';

import { RenderChildren } from './layoutUtils';

const isVerticalLayout = (
  element: UISchemaElement
): element is VerticalLayout => element.type === 'VerticalLayout';

type VerticalLayoutRendererProps = Omit<RendererProps, 'uischema'> & {
  uischema: VerticalLayout;
};

export const VerticalLayoutRenderer = (props: RendererProps) => {
  const { data: _data, uischema, ...otherProps } = props;

  if (!isVerticalLayout(uischema)) {
    return null;
  }

  // We don't hand over data to the layout renderer to avoid re-rendering it with every data change
  return (
    <VerticalLayoutRendererComponent {...otherProps} uischema={uischema} />
  );
};

const VerticalLayoutRendererComponent: FunctionComponent<VerticalLayoutRendererProps> =
  React.memo(function VerticalLayoutRendererComponent({
    schema,
    uischema,
    path,
    visible,
    enabled,
  }) {
    const { renderers, cells } = useJsonForms();

    return (
      <div
        data-testid={'vertical-layout'}
        hidden={visible === undefined || visible === null ? false : !visible}
      >
        <RenderChildren
          layout={uischema}
          renderers={renderers}
          cells={cells}
          schema={schema}
          path={path}
          enabled={enabled}
        />
      </div>
    );
  });
